/**
 * N2LandingPage — the WFS public entry point at /n2 (and /, via redirect).
 *
 * Structure mirrors the original LandingPage (Hero → Who Are You → Journey
 * → Pricing → Research → Footer) but re-typeset in the /n2 cream + serif
 * design system so the public surface is one visual identity.
 *
 * Notches-specific product content (Weekly hero, scorecard chart,
 * methodology, desk bridge) doesn't live here anymore — those already
 * have dedicated pages at /n2/scorecard, /n2/briefings, /n2/research,
 * /n2/methodology. The Research section (bottom) links out to them.
 *
 * Copy: adapted from the retired components/LandingPage.tsx (2026-07-28),
 * updated where products have moved (Premier tier naming, per-symbol SKUs
 * per reference-subscription-bundling-model). No fabricated stats.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import {
  SectionLabel, PrimaryButton, SecondaryButton, Eyebrow, PageTitle, PageLede,
  SectionHead, Body,
} from './N2Primitives';

export const N2LandingPage: React.FC = () => (
  <N2Frame page="landing">
    <Hero />
    <StatsStrip />
    <WhoAreYouSection />
    <ResearchSection />
    <PricingSection />
  </N2Frame>
);

// ---- Hero ------------------------------------------------------------------

const Hero: React.FC = () => {
  const { token } = useAuth();
  return (
    <section style={{
      maxWidth: 1100, margin: '0 auto', padding: '96px 32px 48px',
      textAlign: 'center',
    }}>
      <Eyebrow>Wealth Financial Systems</Eyebrow>
      <PageTitle italic="managed books.">
        Quantitative research + risk tooling for content partners and
      </PageTitle>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <PageLede>
          Published methodology. Published losing periods. Every reading
          scored at its own horizon. Built for creators who want to bring
          their audience to serious tools, and for advisories running real
          books who need reasoning they can bring to a compliance review.
        </PageLede>
      </div>
      <div style={{
        display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap',
        marginTop: 8,
      }}>
        {token ? (
          <>
            <PrimaryButton to="/notches/dashboard">Go to your dashboard</PrimaryButton>
            <SecondaryButton to="/n2/briefings">Read today's briefing</SecondaryButton>
          </>
        ) : (
          <>
            <PrimaryButton to="/contact?source=n2-landing">Talk to us</PrimaryButton>
            <SecondaryButton to="/n2/briefings">Read today's briefing</SecondaryButton>
          </>
        )}
      </div>
    </section>
  );
};

// ---- Stats strip (subtle credibility band) ---------------------------------

const StatsStrip: React.FC = () => (
  <section style={{
    borderTop: `1px solid ${palette.line}`, borderBottom: `1px solid ${palette.line}`,
    background: palette.paper2, padding: '32px 32px',
  }}>
    <div style={{
      maxWidth: 1100, margin: '0 auto',
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24,
    }} className="n2-stats">
      {STATS.map((s) => (
        <div key={s.label} style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: 'var(--serif)', fontSize: 30, fontWeight: 500,
            color: palette.ink, letterSpacing: '-0.02em',
          }}>{s.val}</div>
          <div style={{
            fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 600,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: palette.ink3, marginTop: 6,
          }}>{s.label}</div>
        </div>
      ))}
    </div>
  </section>
);

const STATS = [
  { val: '$0',    label: 'To get started' },
  { val: '5',     label: 'Stages to freedom' },
  { val: '4',     label: 'Research models' },
  { val: 'Swing', label: 'Trade focused' },
];

// ---- Who Are You? (4 audience segments) ------------------------------------

interface Audience {
  label: string;
  title: string;
  body: string;
  bullets: string[];
  link: string;
  cta: string;
}

// Two audiences by design (2026-07-28 pivot from 4-segment).
// Retail traders are served indirectly via the Influencer channel + direct
// signup at /register; the landing itself doesn't market to retail.
const AUDIENCES: Audience[] = [
  {
    label: 'For managed books',
    title: 'You run a real book',
    body: 'Advisories, family offices, prop desks running $500k+ where you need defensible research you can bring to a compliance review — not another feed of trade ideas.',
    bullets: [
      'Published methodology · every model documented',
      'Every reading scored at its own horizon · losses named',
      'Custom research on request · dedicated support',
      'Direct line to the desk that builds the models',
    ],
    link: '/enterprise',
    cta: 'Learn more',
  },
  {
    label: 'For content partners',
    title: 'You bring the audience',
    body: 'Creators serving retail traders — YouTube, X, newsletters, Discord. We do the research + platform; you get white-label + revenue share on every subscriber your audience brings.',
    bullets: [
      'White-label under your brand',
      'Revenue share on every subscriber',
      'Weekly research you can point to (published, credible)',
      'You focus on the audience; we handle the tools',
    ],
    link: '/influencer',
    cta: 'Learn more',
  },
];

const WhoAreYouSection: React.FC = () => (
  <section style={{
    maxWidth: 1100, margin: '0 auto', padding: '96px 32px 0',
  }}>
    <SectionLabel left="Who we work with" right="two audiences, on purpose" />
    <div style={{ marginTop: 8, marginBottom: 40 }}>
      <SectionHead italic="serve.">Who we</SectionHead>
      <Body>
        We're focused. Two audiences at the core — advisories running real
        books, and content creators who bring their audience to serious
        tools. Retail traders are served through our partners.
      </Body>
    </div>
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24,
    }} className="n2-audiences">
      {AUDIENCES.map((a) => (
        <Link
          key={a.label}
          to={a.link}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <div
            style={{
              background: palette.paper, border: `1px solid ${palette.line}`,
              borderRadius: 4, padding: '36px 32px 30px', position: 'relative',
              height: '100%', display: 'flex', flexDirection: 'column',
              transition: 'border-color 150ms',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = palette.goldDeep; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = palette.line; }}
          >
            <div style={{
              position: 'absolute', top: 0, left: 32, width: 32, height: 3,
              background: palette.gold,
            }} />
            <div style={{
              fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: palette.goldDeep, marginBottom: 14,
            }}>{a.label}</div>
            <h3 style={{
              fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 26,
              lineHeight: 1.2, margin: '0 0 14px', color: palette.ink,
              letterSpacing: '-0.01em',
            }}>{a.title}</h3>
            <p style={{
              fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.55,
              color: palette.ink2, margin: '0 0 20px',
            }}>{a.body}</p>
            <ul style={{
              listStyle: 'none', margin: '0 0 24px', padding: 0,
              borderTop: `1px solid ${palette.line}`, flex: 1,
            }}>
              {a.bullets.map((b, i) => (
                <li key={i} style={{
                  fontFamily: 'var(--sans)', fontSize: 13, lineHeight: 1.55,
                  color: palette.ink2, padding: '10px 0',
                  borderBottom: `1px dashed ${palette.line}`,
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                  <span style={{ color: palette.goldDeep, marginTop: 2 }}>◆</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div style={{
              fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
              color: palette.goldDeep, letterSpacing: '0.02em',
            }}>{a.cta} →</div>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

// ---- Journey (5 stages with interactive picker) ----------------------------

type Tier = 'free' | 'premier' | 'pro';
interface Tool { name: string; route: string; desc: string; tier: Tier; }
interface Stage {
  key: string; num: string; name: string; title: string;
  description: string; tools: Tool[];
}

const STAGES: Stage[] = [
  {
    key: 'know', num: 'I', name: 'Know',
    title: 'Know where you stand',
    description: 'Before you build, see the full picture. Track every asset, every liability, and understand your true financial position.',
    tools: [
      { name: 'Net Worth Tracker', route: '/net-worth',   desc: 'Track assets & liabilities', tier: 'free' },
      { name: 'Playbook',          route: '/playbook',    desc: 'Step-by-step wealth guide',  tier: 'free' },
    ],
  },
  {
    key: 'plan', num: 'II', name: 'Plan',
    title: 'Plan your path',
    description: 'Map out your investment lifecycle — from early accumulation through retirement. A plan turns intention into action.',
    tools: [
      { name: 'Lifecycle Planner', route: '/lifecycle-investment', desc: 'Age-based investment plan', tier: 'free' },
    ],
  },
  {
    key: 'build', num: 'III', name: 'Build',
    title: 'Build your Freedom Number',
    description: 'Calculate exactly how much you need to never work again. Structure your retirement with the 4-Bucket System so you never sell in a crash.',
    tools: [
      { name: '4-Bucket Guide',          route: '/learn/4-bucket-system', desc: 'Learn the system first',       tier: 'free' },
      { name: 'Bucket Visualizer',       route: '/bucket-visualizer',     desc: 'Allocation across 4 buckets',  tier: 'free' },
      { name: 'FIRE Investment Strategy', route: '/fire-calculator',      desc: 'Financial independence planner', tier: 'premier' },
    ],
  },
  {
    key: 'monitor', num: 'IV', name: 'Monitor',
    title: 'Monitor the markets',
    description: 'Stay ahead with quantitative research, daily briefings, and research plans that cover the markets you actually trade. Where passive income meets active insight.',
    tools: [
      { name: 'Notches Briefings',  route: '/n2/briefings',       desc: 'Daily read on regime + positioning', tier: 'free' },
      { name: 'Notches Scorecard',  route: '/n2/scorecard',       desc: 'Running record with losses',         tier: 'free' },
      { name: 'Options Research',   route: '/options-research',   desc: 'Hourly IV-based reads',              tier: 'premier' },
      { name: 'Stock Research',     route: '/stock-research',     desc: 'Technical + fundamental picks',      tier: 'premier' },
    ],
  },
  {
    key: 'automate', num: 'V', name: 'Automate',
    title: 'Build your own algo policies',
    description: 'Design trading strategies as policies you control. Pick a strategy, set risk limits, and optionally enable AI to score each opportunity — all based on rules you define.',
    tools: [
      { name: 'Policy Engine',    route: '/algo/engines',   desc: 'Define strategies + risk + execution', tier: 'pro' },
      { name: 'Reading Monitor',  route: '/algo/signals',   desc: 'See what your policies detect',        tier: 'pro' },
      { name: 'Execution Log',    route: '/algo/trades',    desc: 'Every trade your engine makes',        tier: 'pro' },
      { name: 'Performance',      route: '/algo/portfolio', desc: 'How your policies actually perform',   tier: 'pro' },
    ],
  },
];

const JourneySection: React.FC = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const stage = STAGES[activeIdx];
  return (
    <section style={{
      background: palette.paper2,
      borderTop: `1px solid ${palette.line}`, borderBottom: `1px solid ${palette.line}`,
      padding: '96px 32px', marginTop: 96,
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionLabel left="The journey" right="every tool maps to a stage" />
        <div style={{ marginTop: 8, marginBottom: 40 }}>
          <SectionHead italic="freedom.">5 stages to financial</SectionHead>
          <Body>
            Every tool in the system maps to a stage in your wealth-building
            journey. Start where you are; add the next stage when you're ready.
          </Body>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap',
          marginBottom: 32,
        }}>
          {STAGES.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setActiveIdx(i)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 18px',
                background: i === activeIdx ? palette.ink : palette.paper,
                color: i === activeIdx ? palette.paper : palette.ink,
                border: `1px solid ${i === activeIdx ? palette.ink : palette.line}`,
                borderRadius: 4, cursor: 'pointer',
                fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600,
                letterSpacing: '0.03em',
                transition: 'all 120ms',
              }}
            >
              <span style={{
                fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15,
                color: i === activeIdx ? palette.gold : palette.goldDeep,
              }}>{s.num}</span>
              <span style={{ textTransform: 'uppercase' }}>{s.name}</span>
            </button>
          ))}
        </div>

        <div style={{
          background: palette.paper, border: `1px solid ${palette.line}`,
          borderRadius: 4, padding: '36px 36px 32px',
        }} key={stage.key}>
          <h3 style={{
            fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 26,
            margin: '0 0 10px', color: palette.ink, letterSpacing: '-0.01em',
          }}>{stage.title}</h3>
          <p style={{
            fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.6,
            color: palette.ink2, margin: '0 0 24px', maxWidth: 640,
          }}>{stage.description}</p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(230px, 1fr))`,
            gap: 12,
          }}>
            {stage.tools.map((tool) => (
              <Link
                key={tool.route}
                to={tool.route}
                style={{
                  textDecoration: 'none', color: 'inherit',
                  padding: '14px 16px', border: `1px solid ${palette.line}`,
                  background: palette.paper2, borderRadius: 4,
                  display: 'block', transition: 'border-color 120ms',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = palette.goldDeep; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = palette.line; }}
              >
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'baseline', marginBottom: 4,
                }}>
                  <span style={{
                    fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600,
                    color: palette.ink,
                  }}>{tool.name}</span>
                  <TierPill tier={tool.tier} />
                </div>
                <div style={{
                  fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3,
                }}>{tool.desc}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TierPill: React.FC<{ tier: Tier }> = ({ tier }) => {
  const map = {
    free:    { label: 'Free',    bg: '#e8f0e8', color: '#3a6d3a' },
    premier: { label: 'Premier', bg: '#efe2c2', color: '#8b6f2c' },
    pro:     { label: 'Pro',     bg: '#e2e0f0', color: '#4b4680' },
  }[tier];
  return (
    <span style={{
      fontFamily: 'var(--sans)', fontSize: 9, fontWeight: 700,
      letterSpacing: '0.14em', textTransform: 'uppercase',
      padding: '2px 7px', borderRadius: 3,
      background: map.bg, color: map.color,
    }}>{map.label}</span>
  );
};

// ---- Pricing --------------------------------------------------------------
// Approved bundle catalog (2026-09-14):
// Free baseline · Premier $29.99 (stocks + equity options + reports + SMS)
// · SPX Only $79.99 · Index & ETF Research $99.99 · Futures Options
//   Research $99.99 · All Research $199.99 · Boring Bucket $299 (mentored).
// Algo Engine $49.99/mo/engine remains a separate automated-trading offer
// and is unchanged.

interface PriceTier {
  label: string;
  name: string;
  price: string;
  priceOld?: string;
  cadence: string;
  tagline: string;
  bullets: string[];
  cta: { label: string; to: string; primary?: boolean };
  note?: string;
}

const TIERS: PriceTier[] = [
  {
    label: 'Learn',
    name: 'Free',
    price: '$0',
    cadence: '/ forever',
    tagline: 'Plan your wealth journey.',
    bullets: [
      'Net Worth Tracker',
      'Lifecycle Planner',
      'Wealth Playbook + 4-Bucket Guide',
      'Bucket Visualizer',
      'Notches Briefings (last 3 days)',
      'Notches Scorecard (public)',
    ],
    cta: { label: 'Start free', to: '/register?source=n2-pricing-free' },
  },
  {
    label: 'Stock research',
    name: 'Premier',
    price: '$29.99',
    cadence: '/ month',
    tagline: 'Stocks + equity options + full briefings archive.',
    note: '60-day money-back guarantee',
    bullets: [
      'Everything in Free',
      'Stock swing research + daily briefing',
      'Options (equity) research',
      'Full Notches Briefings archive',
      'Inline "So what:" impact per section',
      'SMS alerting on reports',
      '4 industry watchlists + custom (15 symbols)',
    ],
    cta: { label: 'Start Premier · $29.99/mo', to: '/register?source=n2-pricing-premier', primary: true },
  },
  {
    label: 'Algo engine',
    name: 'Add Algo Trading',
    price: '$49.99',
    priceOld: '$59.99',
    cadence: '/ month per engine',
    tagline: 'Build your own strategy policies. Requires Premier.',
    bullets: [
      'Build your own strategy policies',
      '4 strategy frameworks',
      'AI confidence scoring',
      'Trailing stop + profit protection',
      'Alpaca brokerage execution',
      'Portfolio analytics',
    ],
    cta: { label: 'Add Algo trading', to: '/upgrade' },
  },
];

// Bundle strip — sourced from the shared plansCatalog so this page stays
// consistent with /n2/plans and the Evaluate wizard. Premier is already
// shown in the TIERS grid above; this strip covers the standalone
// specialist plans and the bundles.
const NOTCHES_BUNDLES = [
  {
    key: 'spx-only' as const,
    label: 'SPX Only',
    coverage: 'SPX standalone',
    monthlyPrice: 79.99,
  },
  {
    key: 'es-only' as const,
    label: 'ES Only',
    coverage: 'ES standalone',
    monthlyPrice: 79.99,
  },
  {
    key: 'index-etf-research' as const,
    label: 'Index & ETF Research',
    coverage: 'SPX · SPY · QQQ · IWM',
    monthlyPrice: 99.99,
  },
  {
    key: 'futures-options-research' as const,
    label: 'Futures Options Research',
    coverage: 'ES · MES · GC · CL',
    monthlyPrice: 99.99,
  },
  {
    key: 'all-research' as const,
    label: 'All Research',
    coverage: 'Premier + Index & ETF + Futures Options',
    monthlyPrice: 199.99,
  },
];

const PricingSection: React.FC = () => (
  <section style={{
    background: palette.paper2,
    borderTop: `1px solid ${palette.line}`,
    padding: '80px 32px 96px', marginTop: 96,
  }}>
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
    <SectionLabel left="For individual traders" right="if you found us directly" />
    <div style={{ marginTop: 8, marginBottom: 40 }}>
      <SectionHead italic="the tools.">Individual pricing for</SectionHead>
      <Body>
        Landed here as an individual trader? Free for the public research and
        daily briefings; Premier ($29.99/mo) for stock and equity-options
        coverage; and a set of research bundles for index, ETF, and futures
        options — one active research membership per customer.
      </Body>
    </div>

    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20,
    }} className="n2-pricing">
      {TIERS.map((t) => <PriceCard key={t.label} tier={t} />)}
    </div>

    {/* Research bundle strip — links into the specific plan section on /n2/plans */}
    <div style={{ marginTop: 40 }}>
      <p style={{
        fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: palette.goldDeep, marginBottom: 12, textAlign: 'center',
      }}>Research bundles · one active membership per customer</p>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12,
      }} className="n2-notches-skus">
        {NOTCHES_BUNDLES.map((b) => (
          <Link
            key={b.key}
            to={`/n2/plans#${b.key}`}
            style={{
              textDecoration: 'none', color: 'inherit',
              padding: '20px 18px', border: `1px solid ${palette.line}`,
              background: palette.paper, borderRadius: 4,
              display: 'block', transition: 'border-color 120ms',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = palette.goldDeep; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = palette.line; }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'baseline', marginBottom: 6, gap: 8,
            }}>
              <span style={{
                fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 700,
                letterSpacing: '0.02em', color: palette.ink,
              }}>{b.label}</span>
              <span style={{
                fontFamily: 'var(--serif)', fontSize: 18, color: palette.ink,
                whiteSpace: 'nowrap',
              }}>${b.monthlyPrice.toFixed(2)}<span style={{
                fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3,
              }}>/mo</span></span>
            </div>
            <div style={{
              fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3,
            }}>{b.coverage}</div>
          </Link>
        ))}
      </div>
      <p style={{
        fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3,
        fontStyle: 'italic', textAlign: 'center', marginTop: 16,
      }}>
        SPX Only and ES Only are single-market standalones. SPX is already
        included in Index &amp; ETF Research and All Research; ES is already
        included in Futures Options Research and All Research. Coverage per
        plan is fixed; new markets and products are not automatically added.
      </p>
    </div>

    <p style={{
      fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3,
      fontStyle: 'italic', textAlign: 'center', marginTop: 32,
    }}>
      No long-term contracts · cancel anytime · every paid product includes a
      60-day money-back guarantee.
    </p>
    </div>
  </section>
);

const PriceCard: React.FC<{ tier: PriceTier }> = ({ tier }) => {
  const isPrimary = tier.cta.primary;
  return (
    <div style={{
      background: palette.paper,
      border: `1px solid ${isPrimary ? palette.goldDeep : palette.line}`,
      borderRadius: 4, padding: '32px 28px 28px', position: 'relative',
      boxShadow: isPrimary ? '0 4px 12px rgba(191,149,63,0.08)' : 'none',
      display: 'flex', flexDirection: 'column',
    }}>
      {isPrimary && (
        <div style={{
          position: 'absolute', top: -12, left: 24,
          padding: '4px 12px', background: palette.gold, color: palette.paper,
          fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.16em', textTransform: 'uppercase', borderRadius: 3,
        }}>Most subscribers pick this</div>
      )}
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: palette.goldDeep, marginBottom: 12,
      }}>{tier.label}</div>
      <h3 style={{
        fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 24,
        margin: '0 0 8px', color: palette.ink, letterSpacing: '-0.01em',
      }}>{tier.name}</h3>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4,
      }}>
        {tier.priceOld && (
          <span style={{
            fontFamily: 'var(--serif)', fontSize: 18, color: palette.ink3,
            textDecoration: 'line-through',
          }}>{tier.priceOld}</span>
        )}
        <span style={{
          fontFamily: 'var(--serif)', fontSize: 34, fontWeight: 500,
          color: palette.ink,
        }}>{tier.price}</span>
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
        }}>{tier.cadence}</span>
      </div>
      <p style={{
        fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink2,
        margin: '0 0 12px', lineHeight: 1.5,
      }}>{tier.tagline}</p>
      {tier.note && (
        <div style={{
          fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 600,
          color: palette.goldDeep, letterSpacing: '0.02em', marginBottom: 20,
        }}>{tier.note}</div>
      )}
      <ul style={{
        listStyle: 'none', margin: '0 0 24px', padding: 0,
        borderTop: `1px solid ${palette.line}`, flex: 1,
      }}>
        {tier.bullets.map((b, i) => (
          <li key={i} style={{
            fontFamily: 'var(--sans)', fontSize: 13, lineHeight: 1.55,
            color: palette.ink2, padding: '10px 0',
            borderBottom: `1px dashed ${palette.line}`,
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <span style={{ color: palette.goldDeep, marginTop: 2 }}>◆</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
      {isPrimary
        ? <PrimaryButton to={tier.cta.to}>{tier.cta.label}</PrimaryButton>
        : <SecondaryButton to={tier.cta.to}>{tier.cta.label}</SecondaryButton>}
    </div>
  );
};

// ---- Research section (public Notches + quality studies) -------------------

interface ResearchCard {
  to: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  stat: { value: string; label: string };
  cta: string;
}

const RESEARCH_CARDS: ResearchCard[] = [
  {
    to: '/n2/briefings',
    eyebrow: 'Daily briefings · free',
    title: 'Today\'s read, published',
    subtitle: 'Twice-daily briefing — morning setup + EOD recap. Regime, positioning, and the "So what" for tomorrow. Public today + last 2 days.',
    stat: { value: 'daily', label: 'morning + EOD' },
    cta: 'Read today\'s briefing →',
  },
  {
    to: '/n2/scorecard',
    eyebrow: 'Public scorecard · Notches',
    title: 'Every reading, scored publicly',
    subtitle: 'Four independent models (COT · GEX · IV · AVWAP) with every reading published as it fires and scored at the model\'s own horizon. Losers shown alongside winners.',
    stat: { value: '353', label: 'published readings' },
    cta: 'Open the scorecard →',
  },
  {
    to: '/n2/research',
    eyebrow: 'Notches Weekly · free',
    title: 'Our weekly research letter',
    subtitle: 'A new letter every Sunday. Plain-English read on positioning, gamma, vol regime, and what our models are telling the desk this week.',
    stat: { value: 'weekly', label: 'every Sunday' },
    cta: 'Read the archive →',
  },
  {
    to: '/n2/methodology',
    eyebrow: 'Methodology · free',
    title: 'How the models actually work',
    subtitle: 'For each model — what it measures, the framework, the regimes where it works, the regimes where it fails, and what we don\'t publish.',
    stat: { value: '4', label: 'models documented' },
    cta: 'Read the methodology →',
  },
];

const ResearchSection: React.FC = () => (
  <section style={{
    maxWidth: 1100, margin: '0 auto', padding: '96px 32px 96px',
  }}>
    <SectionLabel left="Research" right="published as it fires" />
    <div style={{ marginTop: 8, marginBottom: 40 }}>
      <SectionHead italic="performs.">How our research actually</SectionHead>
      <Body>
        Free public research from our Notches program. No hand-picked
        highlights — full reading sets and every model output, in plain
        English. Losing periods named, not skipped.
      </Body>
    </div>

    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20,
    }} className="n2-research-cards">
      {RESEARCH_CARDS.map((c) => (
        <Link
          key={c.to}
          to={c.to}
          style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}
        >
          <div
            style={{
              background: palette.paper, border: `1px solid ${palette.line}`,
              borderRadius: 4, padding: '28px 28px 24px', position: 'relative',
              height: '100%', boxSizing: 'border-box',
              display: 'flex', flexDirection: 'column',
              transition: 'border-color 150ms',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = palette.goldDeep; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = palette.line; }}
          >
            <div style={{
              position: 'absolute', top: 0, left: 28, width: 32, height: 3,
              background: palette.gold,
            }} />
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'baseline', marginBottom: 14,
            }}>
              <div style={{
                fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: palette.goldDeep,
              }}>{c.eyebrow}</div>
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 500,
                  color: palette.ink, letterSpacing: '-0.02em',
                }}>{c.stat.value}</div>
                <div style={{
                  fontFamily: 'var(--sans)', fontSize: 10, color: palette.ink3,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>{c.stat.label}</div>
              </div>
            </div>
            <h3 style={{
              fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 22,
              lineHeight: 1.25, margin: '0 0 10px', color: palette.ink,
              letterSpacing: '-0.01em',
            }}>{c.title}</h3>
            <p style={{
              fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.6,
              color: palette.ink2, margin: '0 0 18px', flex: 1,
            }}>{c.subtitle}</p>
            <div style={{
              fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600,
              color: palette.goldDeep,
            }}>{c.cta}</div>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

export default N2LandingPage;
